/**
 * WHERE AN INVITE CODE LIVES BETWEEN ARRIVING AND SIGNING UP.
 *
 * A friend opens `https://www.firstroundko.com/?ref=FRKO…`. The code has to survive
 * until they actually create an account, and on this site that can be several steps
 * later: the gate on the first page sends them to `/auth`, or they read the pricing
 * page first. So the code is captured ONCE when the application boots and kept until
 * a signup consumes it.
 *
 * WHY localStorage RATHER THAN THE URL OR A COOKIE.
 *
 *   * In the URL, it would have to be threaded through every navigation and would
 *     vanish the moment the customer clicked anything.
 *   * In a cookie it would be sent to the server on every request for no reason,
 *     and it would outlive the visit by its own expiry rather than by our choice.
 *   * In localStorage it survives the SPA's client-side navigation, which is exactly
 *     the lifetime that is needed, and it is cleared the moment it has been used.
 *
 * WHAT IT IS NOT. It is not identity and it is not a credential: it says "somebody
 * invited me", and the server independently decides whether that is true. Every
 * failure here -- storage disabled, private mode, a code from months ago -- costs a
 * reward to a referrer, never an account to a customer, which is why every function
 * below swallows its own errors instead of surfacing them.
 */
const STORAGE_KEY = 'frko_referral_code';

/**
 * A cap, not a format check. The server normalises whatever it receives, and
 * duplicating that alphabet here would mean two implementations of one rule that can
 * disagree. The only thing worth refusing locally is an absurd value from a crafted
 * URL that would sit in storage forever.
 */
const MAX_STORED_LENGTH = 64;

/** Remember the `?ref=` this visit arrived with, if it has one. Called once, at boot. */
export const captureReferralCode = () => {
	if (typeof window === 'undefined') return;

	try {
		const code = new URLSearchParams(window.location.search).get('ref');
		if (!code) return;

		const trimmed = code.trim();
		if (trimmed && trimmed.length <= MAX_STORED_LENGTH) {
			localStorage.setItem(STORAGE_KEY, trimmed);
		}
	} catch {
		// Private mode, or storage turned off. The visit simply goes unattributed.
	}
};

/** The code to send with a signup, or null. */
export const storedReferralCode = (): string | null => {
	if (typeof window === 'undefined') return null;

	try {
		return localStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
};

/**
 * Forget the code, AFTER a signup has succeeded.
 *
 * On success and not before: a failed signup (a wrong captcha, a password that is
 * too short) must not lose the invitation, because the customer will try again.
 * Once the account exists the code has done its job, and keeping it would attribute
 * the NEXT account created in this browser -- a colleague on a shared laptop, a
 * second family member -- to a referrer they never met.
 */
export const clearReferralCode = () => {
	if (typeof window === 'undefined') return;

	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// Nothing to do; the value is harmless.
	}
};
